import axiosClient from "utils/axios";
import { ApiResponse } from "types/api.types";
import type {
  CreateEmployeeBankAccountPayload,
  EmployeeBankAccount,
  UpdateEmployeeBankAccountPayload,
} from "../types/employee-bank-account.types";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

export function normalizeBankAccount(raw: unknown): EmployeeBankAccount {
  if (!isRecord(raw)) {
    return {
      id: 0,
      employeeId: 0,
      accountHolderName: "",
      bankName: "",
      branchName: null,
      accountNumber: "",
      ifscCode: "",
      isPrimary: false,
    };
  }
  const isPrimaryRaw = raw.isPrimary ?? raw.is_primary;
  return {
    id: Number(raw.id ?? 0),
    employeeId: Number(raw.employeeId ?? raw.employee_id ?? 0),
    accountHolderName: String(
      raw.accountHolderName ?? raw.account_holder_name ?? "",
    ),
    bankName: String(raw.bankName ?? raw.bank_name ?? ""),
    branchName:
      raw.branchName != null
        ? String(raw.branchName)
        : raw.branch_name != null
          ? String(raw.branch_name)
          : null,
    accountNumber: String(raw.accountNumber ?? raw.account_number ?? ""),
    ifscCode: String(raw.ifscCode ?? raw.ifsc_code ?? ""),
    isPrimary: Boolean(isPrimaryRaw),
  };
}

function unwrapListData(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (isRecord(data) && Array.isArray(data.rows)) return data.rows;
  return [];
}

/**
 * Paths match Express: `employeeRoutes.use("/:employeeId/bank-accounts", …)`
 * under the parent mount, e.g. `app.use("/employees", employeeRoutes)`
 * → `/employees/:employeeId/bank-accounts`.
 */
export const listEmployeeBankAccounts = async (employeeId: number) => {
  const response = await axiosClient.get<ApiResponse<unknown>>(
    `/employees/${employeeId}/bank-accounts`,
  );
  const rows = unwrapListData(response.data.data).map(normalizeBankAccount);
  return { ...response.data, data: rows } as ApiResponse<EmployeeBankAccount[]>;
};

export const getEmployeeBankAccount = async (
  employeeId: number,
  bankAccountId: number,
) => {
  const response = await axiosClient.get<ApiResponse<unknown>>(
    `/employees/${employeeId}/bank-accounts/${bankAccountId}`,
  );
  return {
    ...response.data,
    data: normalizeBankAccount(response.data.data),
  } as ApiResponse<EmployeeBankAccount>;
};

export const createEmployeeBankAccount = async (
  employeeId: number,
  payload: CreateEmployeeBankAccountPayload,
) => {
  const response = await axiosClient.post<ApiResponse<unknown>>(
    `/employees/${employeeId}/bank-accounts`,
    payload,
  );
  return {
    ...response.data,
    data: normalizeBankAccount(response.data.data),
  } as ApiResponse<EmployeeBankAccount>;
};

export const updateEmployeeBankAccount = async (
  employeeId: number,
  bankAccountId: number,
  payload: UpdateEmployeeBankAccountPayload,
) => {
  const response = await axiosClient.put<ApiResponse<unknown>>(
    `/employees/${employeeId}/bank-accounts/${bankAccountId}`,
    payload,
  );
  return {
    ...response.data,
    data: normalizeBankAccount(response.data.data),
  } as ApiResponse<EmployeeBankAccount>;
};

export const deleteEmployeeBankAccount = async (
  employeeId: number,
  bankAccountId: number,
) => {
  const response = await axiosClient.delete<ApiResponse<unknown>>(
    `/employees/${employeeId}/bank-accounts/${bankAccountId}`,
  );
  return response.data;
};
